import React, { useEffect, useState, useRef } from 'react'
import { HiOutlineDownload } from 'react-icons/hi'
import { VscFilePdf, VscFileZip, VscFile } from 'react-icons/vsc'
import { useDrag, useDrop } from 'react-dnd'

const BYTE = 1000
const getKB = (bytes) => Math.round(bytes / BYTE)

const FileIcon = ({ children }) => {
    return <span className="text-4xl">{children}</span>
}

const FileItem = (props) => {
    const { file, children, large, index, moveFile, draggableEnabled = false } = props
    const { url, type, name, size } = file

    const ref = useRef(null)
    const [fileData, setFileData] = useState({ url, type, name: name || file.filename, size })

    // Drag and Drop functionality
    const [{ isDragging }, drag] = useDrag({
        type: 'FILE_ITEM',
        item: () => ({ index }),
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        canDrag: draggableEnabled,
    })

    const [{ isOver }, drop] = useDrop({
        accept: 'FILE_ITEM',
        hover: (item) => {
            if (!ref.current) {
                return
            }
            const dragIndex = item.index
            const hoverIndex = index

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return
            }

            // Time to actually perform the action
            moveFile(dragIndex, hoverIndex)

            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
        canDrop: () => draggableEnabled,
    })

    // Combine drag and drop refs
    if (draggableEnabled) {
        drag(drop(ref))
    }

    const renderThumbnail = () => {
        const isImageFile = fileData.type
            ? fileData.type.split('/')[0] === 'image'
            : false
        if (isImageFile) {
            return (
                <img
                    className="upload-file-image"
                    src={
                        fileData.url ? fileData.url : URL.createObjectURL(file)
                    }
                    alt={`file preview ${fileData.name}`}
                />
            )
        }

        if (fileData.type === 'application/zip') {
            return (
                <FileIcon>
                    <VscFileZip />
                </FileIcon>
            )
        }

        if (fileData.type === 'application/pdf') {
            return (
                <FileIcon>
                    <VscFilePdf />
                </FileIcon>
            )
        }

        return (
            <FileIcon>
                <VscFile />
            </FileIcon>
        )
    }

    const getData = async () => {
        if (url) {
            const response = await fetch(url)
            const blob = await response.blob()
            setFileData((e) => ({ ...e, size: blob.size, type: blob.type }))
        }
    }

    useEffect(() => {
        getData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div
            ref={ref}
            className={`upload-file ${isDragging ? 'dragging' : ''} ${isOver ? 'drag-over' : ''}`}
            style={{
                cursor: draggableEnabled ? 'move' : 'default',
            }}
        >
            <div className="flex">
                <div className="upload-file-thumbnail overflow-hidden">{renderThumbnail()}</div>
                {!large && (
                    <div className="upload-file-info">
                        <h6 className="upload-file-name">{fileData.name}</h6>
                        <span className="upload-file-size">
                            {getKB(fileData.size ?? 0)} kb
                        </span>
                    </div>
                )}
            </div>
            <div className="flex">
                {url ? (
                    <a
                        href={fileData.url}
                        className={'close-btn upload-file-remove'}
                        role="button"
                        download={fileData.name}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <HiOutlineDownload className="text-lg" />
                    </a>
                ) : (
                    ''
                )}
                {children}
            </div>
        </div>
    )
}

export default FileItem
